import { useMemo } from 'react'
import { FunnelChartBase } from 'src/components/analytics/charts/Funnel'
import { NoData } from 'src/components/analytics/common/NoData'
import { FunnelChartData } from 'src/components/analytics/types/funnel'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { ChartSkeleton } from '../common/Skeleton'
import { percentageWithFixedDecmials } from 'src/lib/utils/analytics'

const CONVERSION_FUNNEL_OBJECT = {
  checkout_initiated: 'Checkout initiated',
  login_completed: 'Login completed',
  address_selected: 'Address submitted',
  payment_method_selected: 'Payment method selected',
  order_completed: 'Order completed'
}

export default function FunnelChart() {
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.conversion_funnel,
      dependencies: {
        endpoints: ['/conversion-funnel']
      }
    },

    refreshKey
  })

  const funnelData = useMemo(() => {
    const rawData = data[0]?.data?.result?.widgetData?.data?.[0]?.data
    const rawComparedData = data[0]?.data?.result?.widgetData?.compared_data?.[0]?.data
    if (!rawData) return [] as FunnelChartData[]

    // Create an array of keys in the desired order
    const orderedKeys = Object.keys(CONVERSION_FUNNEL_OBJECT)

    const transformedData: FunnelChartData[] = orderedKeys
      .map(key => {
        const item = rawData.find((item: [string, number, number]) => item[0] === key)
        if (!item) return null

        const [name, value, percentage] = item
        const label = CONVERSION_FUNNEL_OBJECT[key as keyof typeof CONVERSION_FUNNEL_OBJECT]
        let comparedToPastValue: number | null = null

        if (rawComparedData) {
          comparedToPastValue = rawComparedData.find((item: [string, number, number]) => item[0] === name)?.[1]
        }

        return {
          name: label,
          value: value.toString(),
          percentage: percentage.toString(),
          comparedToPastPercentage: `${percentageWithFixedDecmials(value, comparedToPastValue ?? 0).toString()}%`
        }
      })
      .filter((item): item is FunnelChartData => item !== null)

    return transformedData
  }, [data])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (funnelData.length > 0) {
      return <FunnelChartBase data={funnelData} />
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}
